import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import CrudTable from "../components/CrudTable";
import { AlertTriangle, Info, Settings, X, RefreshCw, RotateCcw, Undo2 } from "lucide-react";

const trTarih = (s) => { if (!s) return "—"; const p = String(s).slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : s; };
const fmt = (n) => Number(n || 0).toLocaleString("tr-TR");
const BOLUMLER = ["Dekor", "Dijital", "Sırlama", "Logo"];

const DurumBadge = ({ r }) => {
  const red = r.durum === "gecikmis";
  return (
    <div className="flex flex-col gap-0.5">
      <span data-testid={`kg-durum-${r.urun_kodu}-${r.tarih}`} className={`inline-block w-fit rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap ${red ? "bg-red-100 text-red-700 border border-red-200" : "bg-sky-100 text-sky-700 border border-sky-200"}`}>
        {red ? "Gecikmiş / Hatalı" : "Fırında"}
      </span>
      {red && r.neden && (
        <span className="inline-flex items-center gap-1 text-[10px] text-red-500 font-semibold" title={r.neden} data-testid={`kg-neden-${r.urun_kodu}-${r.tarih}`}>
          <Info size={10} className="shrink-0" /> {r.neden}
        </span>
      )}
    </div>
  );
};

const columns = [
  { key: "tarih", label: "Yapılış Tarihi", value: (r) => trTarih(r.tarih), render: (r) => <span className="mono">{trTarih(r.tarih)}</span> },
  { key: "urun_kodu", label: "Ürün Kodu", mono: true },
  { key: "renk", label: "Renk" },
  { key: "bolum", label: "Bölüm" },
  { key: "yapilan", label: "Yapılan Adet", num: true, render: (r) => <span className="font-semibold text-indigo-700">{fmt(r.yapilan)}</span> },
  { key: "kalite1", label: "1.Kalite", num: true, render: (r) => <span className="text-green-700">{fmt(r.kalite1)}</span> },
  { key: "kalite2", label: "2.Kalite", num: true, render: (r) => <span className="text-sky-700">{fmt(r.kalite2)}</span> },
  { key: "kalite3", label: "3.Kalite", num: true, render: (r) => <span className="text-amber-700">{fmt(r.kalite3)}</span> },
  { key: "fire", label: "Iskarta", num: true, render: (r) => <span className="text-red-600">{fmt(r.fire)}</span> },
  { key: "gecen", label: "Toplam Geçen", num: true, render: (r) => <span className="font-semibold text-zinc-700">{fmt(r.gecen)}</span> },
  { key: "kalan", label: "Kalan (Geçmeyen)", num: true, render: (r) => <span className={`font-bold ${r.durum === "gecikmis" ? "text-red-700" : "text-amber-700"}`}>{fmt(r.kalan)}</span> },
  { key: "gecen_gun", label: "Geçen Gün", num: true, render: (r) => <span className={`font-bold ${r.durum === "gecikmis" ? "text-red-600" : "text-sky-600"}`}>{r.gecen_gun}</span> },
  { key: "gun", label: "İzin (gün)", value: (r) => `${r.gun}` },
  { key: "son_kalite_tarihi", label: "Son Kalite Tarihi", value: (r) => trTarih(r.son_kalite_tarihi), render: (r) => <span className="mono text-zinc-500">{trTarih(r.son_kalite_tarihi)}</span> },
  { key: "durum", label: "Durum", value: (r) => (r.durum === "gecikmis" ? "Gecikmiş / Hatalı" : "Fırında"), render: (r) => <DurumBadge r={r} /> },
  { key: "neden", label: "Olası Neden", value: (r) => (r.durum === "gecikmis" ? (r.neden || "") : "") },
];

export default function KalitedenGecmeyen() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [ayarOpen, setAyarOpen] = useState(false);
  const [ayar, setAyar] = useState({ varsayilan: 2, bolumler: {}, esik: 15 });
  const [ozet, setOzet] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [sifirlananlar, setSifirlananlar] = useState([]);
  const [resetPanelOpen, setResetPanelOpen] = useState(false);

  const loadAyar = () => api.get("/dashboard/kalite-gun-ayari")
    .then((r) => setAyar({ varsayilan: r.data.varsayilan || 2, bolumler: r.data.bolumler || {}, esik: r.data.esik ?? 15 })).catch(() => {});
  const loadOzet = () => api.get("/dashboard/kaliteden-gecmeyen", { params: { ozet: true } })
    .then((r) => setOzet(r.data)).catch(() => {});
  const loadReset = () => api.get("/dashboard/kaliteden-gecmeyen/sifirlananlar")
    .then((r) => setSifirlananlar(r.data || [])).catch(() => {});

  useEffect(() => { loadAyar(); loadOzet(); loadReset(); }, [reloadKey]);

  const sifirla = async (r) => {
    if (!window.confirm(`${r.urun_kodu} — ${trTarih(r.tarih)} (${fmt(r.kalan)} adet) kaliteden geçmeyen kaydı sıfırlansın mı?\n\nBu ürün artık "kaliteden geçmemiş" olarak gösterilmeyecek.`)) return;
    try {
      await api.post("/dashboard/kaliteden-gecmeyen/sifirla", { id: r.id, urun_kodu: r.urun_kodu, tarih: r.tarih, bolum: r.bolum, renk: r.renk, kalan: r.kalan });
      toast.success("Kayıt sıfırlandı");
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sıfırlanamadı"); }
  };

  const topluSifirla = async (rows, clear) => {
    const toplam = rows.reduce((a, r) => a + (Number(r.kalan) || 0), 0);
    if (!window.confirm(`Seçilen ${rows.length} kayıt (${fmt(toplam)} adet) sıfırlansın mı?\n\nBu ürünler artık "kaliteden geçmemiş" olarak gösterilmeyecek.`)) return;
    try {
      const items = rows.map((r) => ({ id: r.id, urun_kodu: r.urun_kodu, tarih: r.tarih, bolum: r.bolum, renk: r.renk, kalan: r.kalan }));
      const res = await api.post("/dashboard/kaliteden-gecmeyen/sifirla-toplu", { items });
      toast.success(`${res.data?.count ?? rows.length} kayıt sıfırlandı`);
      clear();
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sıfırlanamadı"); }
  };

  const geriAl = async (row) => {
    try {
      await api.post("/dashboard/kaliteden-gecmeyen/geri-al", { key: row.key });
      toast.success("Geri alındı");
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Geri alınamadı"); }
  };

  const tableColumns = React.useMemo(() => {
    if (!isAdmin) return columns;
    return [...columns, {
      key: "_sifirla", label: "İşlem", value: () => "",
      render: (r) => (
        <button data-testid={`kg-sifirla-${r.urun_kodu}-${r.tarih}`} onClick={() => sifirla(r)}
          className="inline-flex items-center gap-1 border border-red-300 text-red-600 hover:bg-red-50 text-xs font-semibold px-2 py-1 rounded-sm whitespace-nowrap">
          <RotateCcw size={12} /> Sıfırla
        </button>
      ),
    }];
  }, [isAdmin]);

  const saveAyar = async () => {
    try {
      await api.post("/dashboard/kalite-gun-ayari", ayar);
      toast.success("Kalite süresi güncellendi");
      setAyarOpen(false);
      setReloadKey((k) => k + 1);
    } catch { toast.error("Kaydedilemedi"); }
  };

  const kontrolEt = async () => {
    try {
      const res = await api.post("/dashboard/kalite-gecikme-kontrol");
      const n = res.data?.yeni_bildirim ?? 0;
      if (n > 0) {
        toast.success(`${n} yeni gecikme bildirimi oluşturuldu`, {
          action: { label: "Bildirimleri Gör", onClick: () => navigate("/bildirimler") },
          duration: 8000,
        });
      } else {
        toast.success("Yeni gecikme yok — tüm bildirimler güncel");
      }
    } catch { toast.error("Kontrol başarısız"); }
  };

  return (
    <div className="mt-2" data-testid="kaliteden-gecmeyen-page">
      <div className="flex items-start gap-2 border-l-4 border-sky-500 bg-sky-50 text-sky-800 rounded-sm px-4 py-3 mb-4">
        <Info size={18} className="shrink-0 mt-0.5" />
        <div className="text-sm">
          Üretimden yapılıp <span className="font-bold">henüz kaliteden geçmemiş</span> ürünler (Logo hariç — fırına ürün vermez; sadece Dijital, Dekor, Sırlama).
          Geçen adetler Kalite Takibi'nden alınır (1.Kalite + 2.Kalite + 3.Kalite + Iskarta). Kalite kayıtları, yapılış tarihinden itibaren
          <span className="font-bold"> {ayar.varsayilan} gün</span> (bölüme göre ayarlanabilir) içindeki üretim partisine eşlenir; eski parti önce geçer.
          Süreyi aşan (3+ gün geçmiş) parti <span className="font-bold text-red-600">kırmızı</span>; henüz süre içinde (2 gün ve altı) olan parti <span className="font-bold text-sky-600">mavi "Fırında"</span> ile gösterilir.
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {ozet && (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-red-50 text-red-600 border border-red-200 rounded-full px-3 py-1" data-testid="kg-overdue-count">
              <AlertTriangle size={13} /> {fmt(ozet.gecikmis)} parti gecikmiş / hatalı · {fmt(ozet.gecikmis_kalan)} adet
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={() => setResetPanelOpen((o) => !o)} data-testid="kg-sifirlananlar-btn"
              className="flex items-center gap-1.5 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-xs font-semibold px-3 py-2 rounded-sm">
              <Undo2 size={14} /> Sıfırlananlar ({sifirlananlar.length})
            </button>
          )}
          <button onClick={() => setReloadKey((k) => k + 1)} data-testid="kg-refresh"
            className="flex items-center gap-1.5 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-xs font-semibold px-3 py-2 rounded-sm"><RefreshCw size={14} /> Güncelle</button>
          <button onClick={() => setAyarOpen((o) => !o)} data-testid="kg-ayar-btn"
            className="flex items-center gap-1.5 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-xs font-semibold px-3 py-2 rounded-sm"><Settings size={14} /> Kalite Süresi</button>
        </div>
      </div>

      {ayarOpen && (
        <div className="border border-zinc-200 bg-white rounded-sm p-4 mb-4" data-testid="kg-ayar-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-zinc-700">Kalite Süresi Ayarı (üretimden sonra en geç kaç günde kaliteden geçmeli — istediğiniz gün sayısını girin)</div>
            <button onClick={() => setAyarOpen(false)} className="text-zinc-400 hover:text-zinc-700"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-zinc-500 mb-1">Varsayılan (tüm ürünler)</label>
              <input type="number" min="1" max="60" data-testid="kg-ayar-varsayilan" disabled={!isAdmin} value={ayar.varsayilan}
                onChange={(e) => setAyar((a) => ({ ...a, varsayilan: e.target.value === "" ? 1 : Number(e.target.value) }))}
                className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono bg-white disabled:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-amber-600" />
            </div>
            {BOLUMLER.filter((b) => b !== "Logo").map((b) => (
              <div key={b}>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-zinc-500 mb-1">{b}</label>
                <input type="number" min="1" max="60" data-testid={`kg-ayar-${b}`} disabled={!isAdmin}
                  value={ayar.bolumler?.[b] || ""} placeholder={`Varsayılan (${ayar.varsayilan}g)`}
                  onChange={(e) => setAyar((a) => {
                    const bl = { ...(a.bolumler || {}) };
                    if (e.target.value === "") delete bl[b]; else bl[b] = Number(e.target.value);
                    return { ...a, bolumler: bl };
                  })}
                  className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono bg-white disabled:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-amber-600" />
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-100 flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide text-zinc-500 mb-1">Gizleme Eşiği (kalan adet ≤ bu değer gösterilmez)</label>
              <input type="number" min="0" data-testid="kg-ayar-esik" disabled={!isAdmin} value={ayar.esik}
                onChange={(e) => setAyar((a) => ({ ...a, esik: e.target.value === "" ? 0 : Number(e.target.value) }))}
                className="w-40 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono bg-white disabled:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-amber-600" />
            </div>
            <span className="text-xs text-zinc-400 pb-2">Kalan adedi bu değerin altında/eşit olan partiler listeden, sayaçtan ve bildirimlerden gizlenir.</span>
          </div>
          {isAdmin ? (
            <div className="mt-3 flex justify-between items-center gap-2 flex-wrap">
              <button onClick={kontrolEt} data-testid="kg-kontrol-et"
                className="flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-2 rounded-sm"><AlertTriangle size={14} /> Şimdi Kontrol Et (Bildirim Oluştur)</button>
              <button onClick={saveAyar} data-testid="kg-ayar-save" className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-sm">Kaydet</button>
            </div>
          ) : <div className="mt-2 text-xs text-amber-600 font-semibold">Yalnızca yönetici değiştirebilir.</div>}
        </div>
      )}

      {resetPanelOpen && isAdmin && (
        <div className="border border-zinc-200 bg-white rounded-sm p-4 mb-4" data-testid="kg-sifirlananlar-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-zinc-700">Sıfırlanan Kayıtlar — geri almak için "Geri Al" tıklayın</div>
            <button onClick={() => setResetPanelOpen(false)} className="text-zinc-400 hover:text-zinc-700"><X size={16} /></button>
          </div>
          {sifirlananlar.length === 0 ? (
            <div className="text-sm text-zinc-400 py-4 text-center">Henüz sıfırlanmış kayıt yok.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="kg-sifirlananlar-table">
                <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase">
                  <tr><th className="text-left px-3 py-2">Ürün Kodu</th><th className="text-left px-3 py-2">Renk</th><th className="text-left px-3 py-2">Bölüm</th><th className="text-left px-3 py-2">Yapılış Tarihi</th><th className="text-right px-3 py-2">Sıfırlanan Adet</th><th className="text-left px-3 py-2">Sıfırlayan</th><th className="text-right px-3 py-2">İşlem</th></tr>
                </thead>
                <tbody>
                  {sifirlananlar.map((s) => (
                    <tr key={s.key} className="border-t border-zinc-100" data-testid={`kg-reset-row-${s.urun_kodu}-${s.tarih}`}>
                      <td className="px-3 py-2 mono">{s.urun_kodu}</td>
                      <td className="px-3 py-2">{s.renk || "—"}</td>
                      <td className="px-3 py-2">{s.bolum || "—"}</td>
                      <td className="px-3 py-2 mono text-zinc-500">{trTarih(s.tarih)}</td>
                      <td className="px-3 py-2 text-right font-bold text-amber-700">{fmt(s.kalan)}</td>
                      <td className="px-3 py-2 text-xs text-zinc-500">{s.sifirlayan || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => geriAl(s)} data-testid={`kg-geri-al-${s.urun_kodu}-${s.tarih}`}
                          className="inline-flex items-center gap-1 border border-sky-300 text-sky-700 hover:bg-sky-50 text-xs font-semibold px-2 py-1 rounded-sm">
                          <Undo2 size={12} /> Geri Al
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <CrudTable key={reloadKey} listEndpoint="/dashboard/kaliteden-gecmeyen" columns={tableColumns} readOnly
        quickFilters={{ key: "bolum", label: "Bölüm", options: [{ value: "Dijital", label: "Dijital" }, { value: "Dekor", label: "Dekor" }, { value: "Sırlama", label: "Sırlama" }] }}
        selectable={isAdmin}
        bulkActions={isAdmin ? (rows, clear) => (
          <button onClick={() => topluSifirla(rows, clear)} data-testid="kg-toplu-sifirla-btn"
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-3 py-1.5 rounded-sm">
            <RotateCcw size={14} /> Seçilenleri Sıfırla
          </button>
        ) : null}
        titleSingular="Kaliteden Geçmeyen" printTitle="Kaliteden Geçmeyen Ürünler" exportName="Kaliteden Geçmeyen Ürünler" />
    </div>
  );
}
